import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/", label: "首页", end: true },
  { to: "/blacklist", label: "避雷名单" },
  { to: "/guides", label: "维权指南" },
  { to: "/about", label: "关于我们" },
  { to: "/contact", label: "联系提交" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/blacklist?q=${encodeURIComponent(value)}` : "/blacklist");
    setKeyword("");
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap rounded-md px-3 py-2 font-label text-sm transition-colors cursor-pointer ${
      isActive
        ? "bg-primary-100 text-primary-800 font-medium"
        : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
    }`;

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-primary-950 text-primary-100">
        <div className="mx-auto flex max-w-content items-center justify-center gap-2 px-4 py-2 text-center text-xs md:px-6 md:text-sm">
          <i className="ri-alert-line text-primary-300" />
          <span className="font-label">遇到平台异常？第一时间保存转账凭证，并联系支付渠道尝试止付</span>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-content items-center gap-4 px-4 md:h-[72px] md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-radar-line text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">避雷哨</span>
              <span className="mt-0.5 hidden font-label text-[11px] text-foreground-500 sm:block">
                网站黑料 · 风险记录库
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="ml-4 hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form
            onSubmit={handleSearch}
            role="search"
            className="ml-auto hidden items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 transition-colors focus-within:border-primary-400 md:flex md:w-56 lg:w-64"
          >
            <i className="ri-search-line text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索平台名称"
              aria-label="搜索平台名称"
              className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>

          <Link
            to="/contact"
            className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer md:inline-flex"
          >
            <i className="ri-add-circle-line text-base" />
            提交线索
          </Link>

          <button
            type="button"
            aria-label={menuOpen ? "关闭导航菜单" : "打开导航菜单"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
            <form
              onSubmit={handleSearch}
              role="search"
              className="mb-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400"
            >
              <i className="ri-search-line text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索平台名称"
                aria-label="搜索平台名称"
                className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </form>
            <nav aria-label="移动端导航" className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 font-label text-sm transition-colors ${
                      isActive
                        ? "bg-primary-100 font-medium text-primary-800"
                        : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <Link
              to="/contact"
              className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-add-circle-line text-base" />
              提交线索
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}